// @flow
/** @jsx jsx */

import { jsx } from '@emotion/core';
import * as React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Button from 'react-bootstrap/lib/Button';
import validator from 'validator';
import Modal from 'react-bootstrap/lib/Modal';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';
import { Mutation } from 'react-apollo';
import cuid from 'cuid';

import type { CommonActionProps } from './types.actions';
import type { ActionStatus } from '../../types';

import ToggleController from '../../controllers/ToggleController';
import StatusController from '../../controllers/StatusController';
import ButtonAction from './ButtonAction';
import Row from '../Row';
import Col from '../Col';
import DescriptionField from '../DescriptionField';
import SingleField from '../SingleField';
import DropdownField from '../DropdownField';
import StatusMessage from '../StatusMessage';
import Prompt from '../Prompt';
import Loading from '../Loading';
import { editOrderMutation } from '../../graphql/mutations/orders/editOrder.mutation';
import getMenuItems from '../../graphql/queries/getMenuItems.queries';
import getActiveOrderQuery from '../../graphql/queries/getActiveOrder.query';
import messages from '../../config/messages';
import { removeTypenamesFromOrderDet } from '../../utils';
import { DesktopView, MobileView } from '../../config/mediaQuery';

type Props = {
  onOrderCreated: ActionStatus => typeof undefined,
  orderCreateStatus?: ActionStatus,
  item: any,
  buttonText?: string,
  buttonTextLoading?: string,
  buttonTextCompleted?: string,
  OrderCoop: string,
  OrderNote: string,
  OrderPo: string,
  OrderCoopOptions: any,
  loading: boolean,
  onComplete?: () => mixed,
  Det: any,
};

type State = {
  dirty: boolean,
  closing: boolean,
};

const editOrderNotesSchema = Yup.object().shape({
  Po: Yup.string().max(25, "Po can't be longer than 25 chars"),
  Note: Yup.string().max(150, "Note can't be longer than 150 chars"),
  Coop: Yup.string(),
});

class EditOrderNotesAction extends React.Component<
  CommonActionProps & Props,
  State,
> {
  static defaultProps = {
    size: 'xsmall',
    itemHasContent: false,
    buttonText: 'Notes',
    buttonTextLoading: 'Saving notes...',
    buttonTextCompleted: 'Notes saved!',
  };

  closingRequested: TimeoutID;
  closingSuccessed: TimeoutID;

  constructor(props: CommonActionProps & Props) {
    super(props);

    this.state = {
      dirty: false,
      closing: false,
    };
  }

  onCreateCompleted = (options: {
    data: any,
    onToggleVisibility: () => mixed,
    resetForm: () => mixed,
    values: any,
    originalValues: any,
  }) => {
    const { onToggleVisibility, resetForm, values, originalValues } = options;

    this.props.onOrderCreated('completed');

    this.setState({
      closing: true,
    });

    // local clean up
    this.resetForm();

    if (this.closingRequested) {
      clearTimeout(this.closingRequested);
    }

    this.closingRequested = setTimeout(() => {
      this.onExit({
        onToggleVisibility,
        loading: false,
        dirty: false,
        resetForm,
        values,
        originalValues,
      });
    }, 2000);
  };

  resetForm = () => {
    this.setState({
      dirty: false,
    });

    if (this.closingSuccessed) {
      clearTimeout(this.closingSuccessed);
    }

    this.closingSuccessed = setTimeout(() => {
      this.setState({
        closing: false,
      });
    }, 500);
  };

  onExit = (options: {
    onToggleVisibility: () => mixed,
    dirty: boolean,
    loading: boolean,
    resetForm?: () => mixed,
    force?: boolean,
    values?: any,
    originalValues?: any,
  }) => {
    const {
      onToggleVisibility,
      dirty,
      force,
      resetForm,
      loading,
      values,
      originalValues,
    } = options;

    // if the mutation hasn't finished this will avoid closing the modal until
    // the transaction has been completed.
    if (loading) return;

    if (dirty && !force) {
      this.setState({
        dirty,
      });

      return;
    }

    const hasCoopValueChanged =
      originalValues && values && originalValues.Coop !== values.Coop;

    /**
     * The reason we are checking for the existence of this.props.onComplete is because
     * if it exists, it means that we'll have to refetch the root collection, which most
     * likely it's gonna be the "Order" collection. This happens because the action we
     * are performing, like adding a new item or changing the Coop# might affect the logic
     * for calculating the price of each item. As the time of writing, this only happens
     * on NJ orders where the pricing is calculated (among other things) on the total
     * amount of items, if the coop exits, etc.
     */
    if (typeof this.props.onComplete === 'function' && hasCoopValueChanged) {
      this.props.onComplete();
    }

    onToggleVisibility();

    // formik's reset form
    if (typeof resetForm === 'function') {
      resetForm();
    }

    // local clean up
    this.resetForm();
  };

  componentWillUnmount() {
    if (this.closingRequested) {
      clearTimeout(this.closingRequested);
    }

    if (this.closingSuccessed) {
      clearTimeout(this.closingSuccessed);
    }
  }

  cancelConfirmPrompt = () => {
    this.setState({
      dirty: false,
    });
  };

  onCompleteHandler = (
    originalValues: any,
    handler: (a: any, b: any) => any,
  ) => (dataMutation: any) => handler(dataMutation, originalValues);

  render() {
    return (
      <ToggleController>
        {({ onToggleVisibility, visible }) => (
          <Formik
            initialValues={{
              Note: this.props.OrderNote && validator.unescape(this.props.OrderNote),
              Coop: this.props.OrderCoop,
              Po: this.props.OrderPo,
              ShipTo: this.props.ShipTo,
            }}
            validationSchema={editOrderNotesSchema}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              isValid,
              isInitialValid,
              setFieldValue,
              setFieldTouched,
              resetForm,
              dirty,
            }) => (
              <Mutation
                mutation={editOrderMutation}
                variables={{
                  input: {
                    ...values,
                    Det: removeTypenamesFromOrderDet(this.props.Det),
                  },
                }}
                onCompleted={this.onCompleteHandler(
                  {
                    Note: this.props.OrderNote,
                    Coop: this.props.OrderCoop,
                    Po: this.props.OrderPo,
                  },
                  (dataMutation, originalValues) =>
                    this.onCreateCompleted({
                      data: dataMutation,
                      onToggleVisibility,
                      resetForm,
                      values,
                      originalValues,
                    }),
                )}
                refetchQueries={() => {
                  return [
                    {
                      query: getMenuItems,
                    },
                    {
                      query: getActiveOrderQuery,
                    },
                  ];
                }}
                skip={!visible}
              >
                {(mutation, { loading, error }) => {
                  return (
                    <StatusController hasError={!!error} loading={loading}>
                      {({ status }) => {
                        const isOrderUpdated =
                          status === 'completed' ||
                          this.props.orderCreateStatus === 'completed' ||
                          this.state.closing;

                        return (
                          <React.Fragment>
                            <DesktopView>
                              <ButtonAction
                                bsSize={this.props.size}
                                disabled={this.props.disabled}
                                onClick={onToggleVisibility}
                              >
                                <Glyphicon glyph="pencil" />{' '}
                                <span>
                                  {(status === '' || status === 'error') &&
                                    this.props.buttonText}
                                  {status === 'loading' &&
                                    this.props.buttonTextLoading}
                                  {status === 'completed' &&
                                    this.props.buttonTextCompleted}
                                </span>
                              </ButtonAction>
                            </DesktopView>
                            <MobileView>
                              <button onClick={onToggleVisibility} disabled={this.props.disabled}>
                                <img src='assets/notes.svg' alt='notes icon'/>
                                {(status === '' || status === 'error') &&
                                  this.props.buttonText}
                                {status === 'loading' &&
                                  this.props.buttonTextLoading}
                                {status === 'completed' &&
                                  this.props.buttonTextCompleted}
                              </button>
                            </MobileView>
                            {visible && (
                              <MobileView>
                                {(isMobile) => (
                                  <Modal
                                    show={visible}
                                    onHide={() =>
                                      this.onExit({
                                        onToggleVisibility,
                                        dirty,
                                        resetForm,
                                        loading,
                                      })
                                    }
                                    className="modal--form"
                                    dialogClassName={isMobile ? 'notes-modal': ''}
                                  >
                                    <Modal.Header closeButton={!isMobile}>
                                      <Modal.Title>Order Notes</Modal.Title>
                                    </Modal.Header>
                                    {this.props.loading ? (
                                      <Modal.Body>
                                        <Loading />
                                      </Modal.Body>
                                    ) : (
                                      <Form>
                                        <Modal.Body>
                                          {loading && <Loading />}
                                          {this.state.dirty && (
                                            <Prompt
                                              onCancel={this.cancelConfirmPrompt}
                                              onConfirm={() =>
                                                this.onExit({
                                                  onToggleVisibility,
                                                  resetForm,
                                                  loading,
                                                  dirty: false,
                                                  force: true,
                                                })
                                              }
                                              message={messages.common.unsaved}
                                            />
                                          )}
                                          <DescriptionField
                                            label="Notes"
                                            name="Note"
                                            placeholder=""
                                            errors={errors}
                                            touched={touched}
                                            value={values.Note}
                                            readOnly={false}
                                            inline
                                            fieldProps={{
                                              disabled: isOrderUpdated,
                                            }}
                                          />
                                          {this.props.OrderCoopOptions &&
                                            this.props.OrderCoopOptions.length >
                                            1 && (
                                              <DropdownField
                                                value={values.Coop}
                                                options={
                                                  this.props.OrderCoopOptions
                                                }
                                                id={`coop-${cuid()}`}
                                                title="COOP"
                                                name="Coop"
                                                setFieldValue={setFieldValue}
                                                setFieldTouched={setFieldTouched}
                                                errors={errors}
                                                touched={touched}
                                                disabled={isOrderUpdated}
                                              />
                                            )}
                                          <SingleField
                                            inline
                                            label="PO #"
                                            name="Po"
                                            placeholder=""
                                            errors={errors}
                                            touched={touched}
                                            value={values.Po}
                                            readOnly={false}
                                            fieldProps={{
                                              disabled: isOrderUpdated,
                                            }}
                                          />
                                          {this.props.item.AddressOptions && (
                                            <DropdownField
                                              value={values.ShipTo}
                                              options={
                                                this.props.item.AddressOptions.Addr
                                              }
                                              title="Ship-To"
                                              name="ShipTo"
                                              setFieldValue={setFieldValue}
                                              setFieldTouched={setFieldTouched}
                                              errors={errors}
                                              touched={touched}
                                              disabled={isOrderUpdated}
                                              mobileModal={isMobile}
                                            />
                                          )}
                                        </Modal.Body>
                                        <Modal.Footer>
                                          {!isMobile ?
                                            <Row
                                              css={{
                                                alignItems: 'center',
                                              }}
                                            >
                                              <Col>
                                                {(status === 'completed' ||
                                                  status === '') && (
                                                    <StatusMessage status={status}>
                                                      {messages.editOrderNotes.success}
                                                    </StatusMessage>
                                                  )}
                                                {(status === 'error' ||
                                                  status === '') && (
                                                    <StatusMessage status={status}>
                                                      {messages.editOrderNotes.fail}
                                                    </StatusMessage>
                                                  )}
                                              </Col>
                                              <Col
                                                css={{
                                                  marginLeft: 'auto',
                                                }}
                                              >
                                                <Button
                                                  disabled={
                                                    status !== '' ||
                                                    (!isInitialValid && !isValid)
                                                  }
                                                  onClick={mutation}
                                                >
                                                  {isOrderUpdated && 'Notes saved!'}
                                                  {(status === '' ||
                                                    status === 'error') &&
                                                    !isOrderUpdated &&
                                                    'Save'}
                                                  {status === 'loading' &&
                                                    'Saving changes...'}
                                                </Button>
                                              </Col>
                                            </Row>
                                            :
                                            <div className='columns-bottom-button-group'>
                                              <button
                                                onClick={onToggleVisibility}
                                              >
                                                Cancel
                                              </button>
                                              <button
                                                disabled={
                                                  status !== '' ||
                                                  (!isInitialValid && !isValid)
                                                }
                                                onClick={mutation}
                                              >
                                                {isOrderUpdated && 'Notes saved!'}
                                                {(status === '' ||
                                                  status === 'error') &&
                                                  !isOrderUpdated &&
                                                  'Save'}
                                                {status === 'loading' &&
                                                  'Saving changes...'}
                                              </button>
                                            </div>
                                          }
                                        </Modal.Footer>
                                      </Form>
                                    )}
                                  </Modal>
                                )}
                              </MobileView>
                            )}
                          </React.Fragment>
                        );
                      }}
                    </StatusController>
                  );
                }}
              </Mutation>
            )}
          </Formik>
        )}
      </ToggleController>
    );
  }
}

export default EditOrderNotesAction;
